/**
 * Video embed URL parsing — pure helpers used by cs-markdown to turn a
 * demo-video `<img src="...">` into a player embed.
 *
 * This module has no Angular dependencies on purpose, so the parsing can be
 * unit-tested directly (see video-embed.test.ts), mirroring the pure-text
 * pattern in client/src/utils/url-parser.ts.
 */

// A YouTube video id is always exactly 11 chars: letters, digits, '-', '_'.
const ID_PATTERN = '[A-Za-z0-9_-]{11}';

// Only treat a URL as YouTube when its host (right after the scheme, with an
// optional www./m./music. style subdomain) actually is YouTube. Anchoring on
// the host stops a non-YouTube URL that merely mentions "youtube.com" in a path
// or query param from being mistaken for a video link.
const YOUTUBE_HOST = /^https?:\/\/(?:[\w-]+\.)*(?:youtube(?:-nocookie)?\.com|youtu\.be)\//i;

// Path-style links: youtu.be/<id>, or youtube(-nocookie).com/{embed,shorts,v}/<id>.
// The trailing negative lookahead stops the capture at exactly 11 id chars so a
// longer trailing token (e.g. a stray path segment) cannot bleed into the id.
const PATH_RE = new RegExp(`(?:youtu\\.be|youtube(?:-nocookie)?\\.com/(?:embed|shorts|v))/(${ID_PATTERN})(?![A-Za-z0-9_-])`);

// Query-style links: ...watch?v=<id>. The v param may be first or come after
// other params (e.g. ?feature=share&v=<id>), so we anchor on a '?' or '&'.
const QUERY_RE = new RegExp(`[?&]v=(${ID_PATTERN})(?![A-Za-z0-9_-])`);

/**
 * Pull the 11-char video id out of any common YouTube URL form.
 *
 * Handles youtu.be short/share links (with or without a `?si=` tracking
 * param), the long watch?v= form, youtube.com with or without `www.`/`m.`,
 * the -nocookie host, and the /embed/, /shorts/, and legacy /v/ paths.
 *
 * Returns null when the input is empty or is not a YouTube URL — callers use
 * that to fall through to their other handling (Vimeo, images, INVALID IMAGE).
 */
export function extractYoutubeId(url: string): string | null {
  // if empty or the host isn't YouTube, this is not a YouTube link
  if (!url || !YOUTUBE_HOST.test(url)) return null;
  // if it is a path-style link (youtu.be/<id>, /embed/<id>, /shorts/<id>), return its id
  const path = url.match(PATH_RE);
  if (path) return path[1];
  // otherwise try the watch?v=<id> query form
  const query = url.match(QUERY_RE);
  return query ? query[1] : null;
}

// Whole-string version of ID_PATTERN: matches only when the ENTIRE string is a
// valid 11-char id. extractYoutubeId uses ID_PATTERN as a sub-match inside a
// URL; this anchored version validates a bare id standing on its own.
const ID_EXACT_RE = new RegExp(`^${ID_PATTERN}$`);

/**
 * True when `id` is exactly a valid 11-char YouTube video id and nothing else.
 *
 * extractYoutubeId already validates the ids it pulls out of URLs, so this is a
 * second-line check for code paths that are handed a bare id (the native iOS
 * player service) and want to refuse anything malformed before using it in a
 * watch URL or a plugin call.
 */
export function isValidYoutubeId(id: string): boolean {
  return ID_EXACT_RE.test(id);
}

/**
 * Build the HTML that cs-markdown injects in place of a YouTube demo-video
 * `<img>` tag. Two shapes, chosen by the caller's platform flag:
 *
 * - useNativePlayer = false (desktop, PWA, Android, iOS Safari): the normal
 *   inline youtube-nocookie iframe. This returned string is byte-identical to
 *   what cs-markdown emitted before the native-player change — video-embed.test.ts
 *   pins it so the web/Android embed can never change by accident.
 *
 * - useNativePlayer = true (iOS native app only): a thumbnail with a centered
 *   play overlay instead of an iframe. On iOS native the page lives at
 *   `capacitor://localhost`, which sends no usable Referer, so YouTube's inline
 *   iframe player fails with "Error 153". Tapping the thumbnail calls the
 *   `csPlayNativeYoutube` window-global (registered in cs-markdown.ts), which
 *   opens a full-screen native player instead. See
 *   design-docs/native-youtube-player.md.
 *
 * `videoId` is always an id that extractYoutubeId already validated, so it is
 * safe to drop straight into the markup and the onclick string.
 */
export function buildYoutubeEmbedHtml(videoId: string, useNativePlayer: boolean): string {
  if (!useNativePlayer) {
    return `<div class="aspect-ratio"><iframe src="https://www.youtube-nocookie.com/embed/${videoId}?rel=0&autoplay=0&mute=1" frameborder="0" allowfullscreen></iframe></div>`;
  }
  // ios native: thumbnail image fills the same 16:9 box, with a play overlay on
  // top. the onclick is on the whole container so a tap anywhere opens the
  // native player.
  return `<div class="aspect-ratio cs-video-thumbnail" onclick="csPlayNativeYoutube('${videoId}')"><img src="https://i.ytimg.com/vi/${videoId}/hqdefault.jpg" alt=""><span class="cs-video-thumbnail-play"></span></div>`;
}
